import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Input, 
  Modal, 
  Form, 
  message,
  Breadcrumb,
  Tag,
  Tooltip,
  Row,
  Col,
  Upload,
  Typography
} from 'antd';
import { 
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  ExportOutlined,
  ImportOutlined,
  ArrowLeftOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UploadFile } from 'antd';
import { translationsService, type Translation } from '../../api/translations';

const { TextArea } = Input;
const { Text } = Typography;

const LanguageTranslations: React.FC = () => {
  const navigate = useNavigate();
  const { id: languageId } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState<Translation | null>(null);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Fetch language data
  const { data: language, isLoading: isLanguageLoading } = useQuery({
    queryKey: ['language', languageId],
    queryFn: () => translationsService.getLanguage(languageId!),
    enabled: !!languageId,
  });

  // Fetch translations data
  const { data: translationsData, isLoading: isTranslationsLoading } = useQuery({
    queryKey: ['translations', languageId, searchText],
    queryFn: () => translationsService.getTranslations(languageId!, { 
      search: searchText || undefined,
      limit: 50 
    }),
    enabled: !!languageId,
  });

  // Delete translation mutation
  const deleteTranslationMutation = useMutation({
    mutationFn: (translationKey: string) => 
      translationsService.deleteTranslation(languageId!, translationKey),
    onSuccess: () => {
      message.success('Перевод удален');
      queryClient.invalidateQueries({ queryKey: ['translations', languageId] });
    },
    onError: (error) => {
      console.error('Delete translation error:', error);
      message.error('Ошибка при удалении перевода');
    },
  });

  // Create/Update translation mutation
  const saveTranslationMutation = useMutation({
    mutationFn: (data: { isEdit?: boolean; key: string; value: string }) => {
      if (data.isEdit) {
        return translationsService.updateTranslation(languageId!, data.key, { value: data.value });
      } else {
        return translationsService.createTranslation({
          key: data.key,
          value: data.value,
          languageId: languageId!,
        });
      }
    },
    onSuccess: () => {
      message.success(editingTranslation ? 'Перевод обновлен' : 'Перевод создан');
      queryClient.invalidateQueries({ queryKey: ['translations', languageId] });
      setIsModalVisible(false);
      setEditingTranslation(null);
      form.resetFields();
    },
    onError: (error) => {
      console.error('Save translation error:', error);
      message.error('Ошибка при сохранении перевода');
    },
  });

  // Import translations mutation
  const importTranslationsMutation = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text();
      const translations = JSON.parse(text);
      return translationsService.bulkImportTranslations({
        translations,
        languageId: languageId!,
      });
    },
    onSuccess: (result) => {
      message.success(`Импортировано ${result.imported} переводов`);
      queryClient.invalidateQueries({ queryKey: ['translations', languageId] });
      setIsImportModalVisible(false);
      setFileList([]);
    },
    onError: (error) => {
      console.error('Import error:', error);
      message.error('Ошибка при импорте переводов');
    },
  });

  // Export translations mutation
  const exportTranslationsMutation = useMutation({
    mutationFn: () => translationsService.exportTranslations(languageId!),
    onSuccess: (translations) => {
      const dataStr = JSON.stringify(translations, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `translations_${language?.code || 'unknown'}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success('Переводы экспортированы');
    },
    onError: (error) => {
      console.error('Export error:', error);
      message.error('Ошибка при экспорте переводов');
    },
  });

  const columns = [
    {
      title: 'Ключ',
      dataIndex: 'key',
      key: 'key',
      width: 200,
      render: (key: string) => (
        <Text code style={{ fontSize: '12px' }}>{key}</Text>
      ),
    },
    {
      title: 'Перевод',
      dataIndex: 'value',
      key: 'value',
      ellipsis: {
        showTitle: false,
      },
      render: (value: string) => (
        <Tooltip placement="topLeft" title={value}>
          {value}
        </Tooltip>
      ),
    },
    {
      title: 'Обновлено',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 150,
      render: (date: string) => new Date(date).toLocaleString('ru-RU'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      render: (record: Translation) => (
        <Space>
          <Tooltip title="Редактировать">
            <Button 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button 
              size="small" 
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.key)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleEdit = (translation: Translation) => {
    setEditingTranslation(translation);
    form.setFieldsValue(translation);
    setIsModalVisible(true);
  };

  const handleDelete = (translationKey: string) => {
    console.log('handleDelete called with key:', translationKey);
    Modal.confirm({
      title: 'Удалить перевод?',
      content: 'Это действие нельзя отменить.',
      onOk: () => {
        console.log('Confirming delete for key:', translationKey);
        deleteTranslationMutation.mutate(translationKey);
      },
    });
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      saveTranslationMutation.mutate({
        isEdit: !!editingTranslation,
        key: values.key,
        value: values.value,
      });
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingTranslation(null);
    form.resetFields();
  };

  const handleImport = () => {
    if (fileList.length === 0) {
      message.warning('Выберите файл для импорта');
      return;
    }

    const file = fileList[0].originFileObj;
    if (!file) {
      message.error('Файл не найден');
      return;
    }

    importTranslationsMutation.mutate(file);
  };

  const handleExport = () => {
    exportTranslationsMutation.mutate();
  };

  const filteredData = (translationsData?.items || []).filter((item: Translation) =>
    item.key.toLowerCase().includes(searchText.toLowerCase()) ||
    item.value.toLowerCase().includes(searchText.toLowerCase())
  );

  if (isLanguageLoading || isTranslationsLoading) {
    return <div>Загрузка...</div>;
  }

  if (!language) {
    return <div>Язык не найден</div>;
  }

  return (
    <div className="language-translations">
      <Breadcrumb 
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <Button 
                type="link" 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/translations')}
                style={{ padding: 0 }}
              >
                Переводы
              </Button>
            ),
          },
          {
            title: (
              <Space>
                <Tag color="blue">{language?.code.toUpperCase()}</Tag>
                {language?.nativeName}
              </Space>
            ),
          },
        ]}
      />

      <Card 
        title={
          <Space>
            <Tag color="blue">{language?.code.toUpperCase()}</Tag>
            <span>Переводы - {language?.nativeName}</span>
          </Space>
        }
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setIsModalVisible(true)}
          >
            Добавить перевод
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Поиск по ключу или переводу..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={16} style={{ textAlign: 'right' }}>
            <Space>
              <Button 
                icon={<ImportOutlined />}
                onClick={() => setIsImportModalVisible(true)}
              >
                Импорт
              </Button>
              <Button 
                icon={<ExportOutlined />}
                onClick={handleExport}
                loading={exportTranslationsMutation.isPending}
              >
                Экспорт
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total} переводов`,
          }}
        />
      </Card>

      {/* Модаль редактирования/создания перевода */}
      <Modal
        title={editingTranslation ? 'Редактировать перевод' : 'Добавить перевод'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="key"
            label="Ключ"
            rules={[{ required: true, message: 'Введите ключ перевода' }]}
          >
            <Input 
              placeholder="dashboard, users.add, #.sales..." 
              disabled={!!editingTranslation}
            />
          </Form.Item>

          <Form.Item
            name="value"
            label="Перевод"
            rules={[{ required: true, message: 'Введите перевод' }]}
          >
            <TextArea 
              placeholder="Введите перевод..." 
              rows={3}
              showCount
              maxLength={500}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модаль импорта */}
      <Modal
        title="Импорт переводов"
        open={isImportModalVisible}
        onOk={handleImport}
        onCancel={() => {
          setIsImportModalVisible(false);
          setFileList([]);
        }}
        confirmLoading={importTranslationsMutation.isPending}
        width={500}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            Загрузите JSON файл с переводами в формате: {`{"key": "value", ...}`}
          </Text>
        </div>
        
        <Upload
          accept=".json"
          fileList={fileList}
          onChange={({ fileList: newFileList }) => setFileList(newFileList)}
          beforeUpload={() => false}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>Выбрать файл</Button>
        </Upload>
      </Modal>
    </div>
  );
};

export default LanguageTranslations;
